import Cropper from 'react-easy-crop'
import { useState } from 'react'

/**
 * The ratio this was built for: the hero banner on the exported PDF menu.
 *
 * Kept as the default because it is what every existing caller expects, but it
 * is no longer the only one - a group avatar is a circle, and cropping it to a
 * 3.5:1 letterbox produced an unusable strip.
 */
export const HERO_ASPECT = 794 / 230

/** The crop box never gets wider than this; the height follows the ratio. */
const MAX_WIDTH = 400
const MAX_HEIGHT = 400

export default function ImageCropper({ image, onCropComplete, aspect = HERO_ASPECT, cropShape = 'rect' }: {
  image: File | Blob | string | null;
  onCropComplete: (croppedAreaPixels: { x: number; y: number; width: number; height: number }) => void;
  /** Width / height of the crop box. */
  aspect?: number;
  /** `round` draws a circular mask - the crop itself is still a square. */
  cropShape?: 'rect' | 'round';
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);

  const imageUrl =
    typeof image === 'string' ? image : image && URL.createObjectURL(image);

  /*
   * Sized from the ratio rather than fixed at 400x300. A square crop inside a
   * 4:3 box leaves dead grey on both sides and shrinks the part you are
   * actually aiming at.
   */
  const height = Math.min(MAX_WIDTH / aspect, MAX_HEIGHT);
  const width = height * aspect;

  return (
    <div style={{ position: 'relative', width, height, maxWidth: '100%' }}>
      <Cropper
        image={imageUrl || undefined}
        crop={crop}
        zoom={zoom}
        aspect={aspect}
        cropShape={cropShape}
        onCropChange={setCrop}
        onZoomChange={setZoom}

        onCropComplete={(_, croppedAreaPixels) =>
          onCropComplete(croppedAreaPixels)
        }
        objectFit='contain'
      />
    </div>
  )
}

export async function getCroppedImg(
  imageSrc: string,
  crop: { x: number; y: number; width: number; height: number }
): Promise<Blob> {
  const image = new Image()
  image.src = imageSrc
  await new Promise(resolve => (image.onload = resolve))

  const canvas = document.createElement('canvas')
  canvas.width = crop.width
  canvas.height = crop.height

  const ctx = canvas.getContext('2d')!

  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  )

  return new Promise(resolve => {
    canvas.toBlob(blob => resolve(blob!), 'image/jpeg', 0.9)
  })
}
